import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../api";
import { dateShort, dateTimeLong } from "../ui";

/**
 * LOG — what it actually did, and the five numbers that say whether it works.
 *
 * THE TILES DESCRIBE THE MATCH, NOT THE PAGE. They are counted in SQL with the
 * same predicate that selected the rows, which is the lesson the Leads list
 * learned the expensive way: filtering in the browser made every answer a
 * statement about the 500 rows that happened to be loaded, while the header
 * said "of 500" and looked like it meant "of all your leads".
 *
 * SENDS AND BLOCKS ARE COUNTED SEPARATELY, as the mockup's "41 sends · 2
 * blocked" does. A block is not a send that failed, it is a decision not to
 * send — folding them into one total would make every rate underneath wrong.
 *
 * THE DRAWER IS THE AUDIT TRAIL. Score at send (weights get edited afterwards),
 * the exact copy that went out, which slot they took, and their reply verbatim.
 * It opens beside the table without navigating, which is how records open here.
 */

const OUTCOME_PILL = {
  QUEUED: { label: "Queued", key: "queued" },
  PENDING: { label: "Waiting", key: "pending" },
  REPLIED: { label: "Replied", key: "replied" },
  SCHEDULED: { label: "Scheduled", key: "scheduled" },
  GHOSTED: { label: "Ghosted", key: "ghosted" },
  BOUNCED: { label: "Bounced", key: "bounced" },
  BLOCKED: { label: "Blocked", key: "blocked" },
  CANCELLED: { label: "Cancelled", key: "cancelled" },
  DRY_RUN: { label: "Dry run", key: "dryrun" },
  // "Arrived, and nothing was owed back" — the resting state of a call
  // reminder, whose call has now happened. See migration 040.
  DELIVERED: { label: "Delivered", key: "delivered" },
};

const DATE_LABELS = {
  last_7_days: "Last 7 days",
  last_30_days: "Last 30 days",
  last_90_days: "Last 90 days",
  all_time: "All time",
};

/*
 * The default, for a trigger that has not said otherwise. A call-reminders page
 * gets a shorter list from the server — it cannot book and cannot be ghosted —
 * and the drawer must not offer by hand what the poller is forbidden to infer.
 */
const SETTABLE = ["REPLIED", "SCHEDULED", "GHOSTED", "BOUNCED", "CANCELLED"];

/*
 * The same short labels the Rules tab's preview uses, so a chip reads the same
 * on both surfaces. Slicing the group name to five characters produced "fundi",
 * "keywo" and "headc", which is not a label, it is a truncation.
 */
const GROUP_CHIP = { industry: "Ind", headcount: "HC", title: "Title", funding: "Fund", keywords: "KW" };

/*
 * And the order they render in. jsonb does not preserve object key order, so
 * reading the stored breakdown with Object.entries gave "Title, Fund, Ind, KW,
 * HC" here and "Ind, HC, Title, Fund, KW" on the Rules tab — the same five
 * chips about the same lead, in two different orders.
 */
const GROUP_ORDER = ["industry", "headcount", "title", "funding", "keywords"];

/** "9h 25m" — the unit people actually read a reply time in. */
function duration(minutes) {
  if (minutes === null || minutes === undefined) return "—";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours < 24) return `${hours}h ${String(rest).padStart(2, "0")}m`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
}

/*
 * WHAT THE LOG IS OF, WHICH IS NOT THE SAME SENTENCE FOR EVERY TRIGGER.
 *
 * The table and the tiles are genuinely shared — every automation writes
 * auto_reply_logs, and "sent", "replied", "ghosted" mean the same thing on all
 * of them. The WORDS AROUND THEM were the after-hours reply's, and on a
 * call-reminders page they said things that were not true of it: an empty log
 * explained itself with "a lead that arrives during your working hours is never
 * logged", which is a statement about a different automation entirely, and the
 * A/B variant column has no meaning where there is no A/B.
 *
 * So the copy is asked per trigger and the variant column is dropped where it
 * does not apply. The numbers are untouched.
 */
/*
 * THE TILES ARE THE SAME NUMBERS AND NOT THE SAME QUESTIONS.
 *
 * "Booked rate" and "ghost rate" are only meaningful about an email that was
 * asking for something. A call reminder is not: it cannot cause a booking, and
 * silence after it is the normal and desired outcome rather than a failure.
 * Showing those two tiles on a reminders page would not merely be uninformative
 * — it would report a courtesy that worked as a miss, which is how somebody
 * ends up rewriting copy that was fine.
 *
 * So each trigger declares which tiles mean anything for it, and `third`/`fourth`
 * name what stands in where a shared tile does not apply.
 */
const COPY = {
  call_reminders: {
    empty:
      "No reminders have been planned in this window. Either no calls were booked while it was armed, or the filters are hiding them.",
    // A reminder cannot book a call — the call is why it exists.
    booked: false,
    // And silence after one is the point of it.
    ghost: false,
    replySub: "replies, usually to move the call",
    variants: false,
  },
  call_no_show: {
    empty:
      "No rescues in this window. Either no calls were missed while it was armed, or every one of them had somebody from their side on it.",
    booked: true,
    bookedSub: "rebooked after a rescue",
    ghost: true,
    replySub: "replies to a rescue",
    variants: false,
  },
  after_hours_auto_reply: {
    empty:
      "Either no leads arrived while it was armed, or the filters are hiding them. A lead that arrives during your working hours is never logged — you answer those yourself.",
    booked: true,
    ghost: true,
    replySub: null,
    variants: true,
  },
};

function copyFor(kind) {
  return COPY[kind] || COPY.after_hours_auto_reply;
}

export default function LogTab({ id, kind, settable }) {
  const copy = copyFor(kind);
  const [filters, setFilters] = useState({ date: "last_30_days", outcome: [], variant: [] });
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    try {
      const query = new URLSearchParams({ date: filters.date, limit: "50" });
      if (filters.outcome.length) query.set("outcome", filters.outcome.join(","));
      if (filters.variant.length) query.set("variant", filters.variant.join(","));
      setData(await api(`/automations/${id}/logs?${query.toString()}`));
    } catch (err) {
      setError(err.message);
    }
  }, [id, filters]);

  useEffect(() => {
    load();
  }, [load]);

  if (error) return <div className="crm-card crm-card--warning">{error}</div>;
  if (!data) return <p className="crm-hint">Loading…</p>;

  const { stats } = data;

  return (
    <div className="crm-auto-log">
      <div className="crm-auto-log__top">
        <div className="crm-auto-tiles">
          <Tile label="Sent" value={stats.sent} sub={DATE_LABELS[filters.date].toLowerCase()} />
          <Tile
            label="Reply rate"
            value={stats.reply_rate === null ? "—" : `${stats.reply_rate}%`}
            sub={stats.sent ? `${stats.answered} of ${stats.sent}` : "nothing sent yet"}
          />
          {copy.booked ? (
            <Tile
              label="Booked rate"
              value={stats.booked_rate === null ? "—" : `${stats.booked_rate}%`}
              sub={copy.bookedSub || `${stats.booked} ${stats.booked === 1 ? "meeting" : "meetings"}`}
              accent
            />
          ) : (
            /* What a reminder actually produces: it arrived, and its call came
               and went. The count that answers "is this thing running". */
            <Tile
              label="Delivered"
              value={stats.delivered ?? 0}
              sub="arrived, and the call went ahead"
              accent
            />
          )}
          <Tile
            label="Median time to reply"
            value={duration(stats.median_reply_minutes)}
            /* The A/B comparison, which is the whole reason the split is exact
               rather than random — two samples of very different size would
               make this number meaningless. */
            sub={
              copy.variants
                ? stats.variant_b
                  ? `A: ${duration(stats.median_reply_a)} · B: ${duration(stats.median_reply_b)}`
                  : "A only"
                : copy.replySub || "from the moment it went out"
            }
          />
          {copy.ghost ? (
          <Tile
            label="Ghost rate"
            value={stats.ghost_rate === null ? "—" : `${stats.ghost_rate}%`}
            sub="no reply after 5 working days"
            /* Red only above 40%, per the mockup: a ghost rate is expected to be
               non-zero, and colouring it always would cry wolf. */
            warn={stats.ghost_rate !== null && stats.ghost_rate > 40}
          />
          ) : (
            /* Silence after a reminder is the desired outcome, so there is no
               ghost rate to show. What is worth knowing instead is how often
               one was skipped and for what — the row this automation actually
               produces when it declines to write. */
            <Tile
              label="Skipped"
              value={stats.blocked}
              sub="too late to send, or held back"
              warn={false}
            />
          )}
        </div>

        <div className="crm-auto-filters">
          <Select
            label="Date"
            value={filters.date}
            options={Object.entries(DATE_LABELS).map(([value, label]) => ({ value, label }))}
            onChange={(date) => setFilters({ ...filters, date })}
          />
          <Multi
            label="Outcome"
            values={filters.outcome}
            options={Object.entries(OUTCOME_PILL).map(([value, pill]) => ({ value, label: pill.label }))}
            onChange={(outcome) => setFilters({ ...filters, outcome })}
          />
          {copy.variants && (
            <Multi
              label="Variant"
              values={filters.variant}
              options={[
                { value: "A", label: "A" },
                { value: "B", label: "B" },
              ]}
              onChange={(variant) => setFilters({ ...filters, variant })}
            />
          )}
          <span className="crm-auto-filters__count">
            {/* Sends and blocks, side by side, never summed. */}
            {stats.sent} {stats.sent === 1 ? "send" : "sends"}
            {stats.blocked > 0 && ` · ${stats.blocked} blocked`}
            {stats.cancelled > 0 && ` · ${stats.cancelled} cancelled`}
            {stats.queued > 0 && ` · ${stats.queued} waiting to go`}
          </span>
        </div>
      </div>

      {/*
        The tiles and the filters stay full width; only the TABLE shares a row
        with the drawer. Putting the drawer beside the whole column made the
        tiles reflow into two rows the moment a row was opened — the numbers
        moving because you clicked something unrelated to them.
      */}
      <div className="crm-auto-log__body">
        <div className={`crm-auto-log__main${openId ? " has-drawer" : ""}`}>
        {!data.logs.length ? (
          <div className="crm-card crm-auto-empty">
<h3>Nothing in this window</h3>
            <p className="crm-hint">{copy.empty}</p>
          </div>
        ) : (
          /*
            SCORE, DELAY AND VARIANT ARE THE AFTER-HOURS REPLY'S COLUMNS, and on a
            call automation all three are structurally null: nothing scores a
            reminder (entry is conditions, not a number), nothing delays one (its
            moment comes from the call), and there is no A/B. Three columns of
            em-dashes is not neutral — it reads as missing data rather than as a
            question that was never asked — so they come out, and the row gets a
            modifier that narrows the grid to match.
          */
          <div className="crm-auto-logtable crm-card">
            <div
              className={`crm-auto-logrow crm-auto-logrow--head${copy.variants ? "" : " crm-auto-logrow--calls"}`}
            >
              <div>Sent</div>
              <div>Lead</div>
              {copy.variants && <div className="crm-auto-logscore">Score</div>}
              {copy.variants && <div>Delay</div>}
              {copy.variants && <div>Variant</div>}
              <div>Outcome</div>
              <div>Reply in</div>
            </div>

            {data.logs.map((log) => {
              const pill = OUTCOME_PILL[log.outcome] || { label: log.outcome, key: "pending" };
              const blocked = log.outcome === "BLOCKED";
              return (
                <React.Fragment key={log.id}>
                  <div
                    className={`crm-auto-logrow${openId === log.id ? " is-open" : ""}${
                      copy.variants ? "" : " crm-auto-logrow--calls"
                    }`}
                    role="button"
                    tabIndex={0}
                    onClick={() => setOpenId(openId === log.id ? null : log.id)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setOpenId(openId === log.id ? null : log.id);
                      }
                    }}
                  >
                    <div className="crm-auto-logsent">
                      <b>{dateShort(log.sent_at || log.scored_at)}</b>
                      <span className="crm-num crm-hint">
                        {new Date(log.sent_at || log.scored_at).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                    <div className="crm-auto-loglead">
                      <b>{[log.first_name, log.last_name].filter(Boolean).join(" ") || log.email}</b>
                      <span className="crm-hint">
                        {[log.company_name, log.job_title].filter(Boolean).join(" · ")}
                      </span>
                    </div>
                    {copy.variants && (
                      <div className="crm-auto-logscore">
                        {/* A blocked row's score reads 0 in red — which is also
                            how a block is filtered for (score ≤ 0). */}
                        <span className={`crm-auto-scorechip crm-num${blocked ? " is-blocked" : ""}`}>
                          {log.score}
                        </span>
                      </div>
                    )}
                    {copy.variants && (
                      <div className="crm-num crm-hint">
                        {log.delay_minutes === null ? "—" : `${log.delay_minutes} min`}
                      </div>
                    )}
                    {copy.variants && <div className="crm-hint">{log.template_variant || "—"}</div>}
                    <div>
                      <span className={`crm-pill crm-pill--${pill.key}`}>{pill.label}</span>
                      {log.manual_override && (
                        <span className="crm-hint" title="You set this outcome by hand">
                          {" "}
                          ·&nbsp;yours
                        </span>
                      )}
                    </div>
                    <div className="crm-num crm-hint">{duration(log.time_to_reply_minutes)}</div>
                  </div>

                  {/*
                    A blocked row spends its second line naming what stopped it.
                    The reason is prose written when the block happened, so it
                    still explains itself after the keyword list has changed.
                  */}
                  {blocked && log.blocked_reason && (
                    <div className="crm-auto-logblocked">
                      <span className="crm-label">Blocked</span>
                      <span>{log.blocked_reason}</span>
                    </div>
                  )}
                </React.Fragment>
              );
            })}

            {data.matched > data.logs.length && (
              <div className="crm-pager">
                <span className="crm-hint">
                  Showing {data.logs.length} of {data.matched}
                </span>
              </div>
            )}
          </div>
        )}
        </div>

      {openId && (
        <LogDrawer
          settable={settable}
          id={id}
          logId={openId}
          onClose={() => setOpenId(null)}
          onChanged={() => {
            load();
          }}
        />
      )}
      </div>
    </div>
  );
}

function Tile({ label, value, sub, accent = false, warn = false }) {
  return (
    <div className={`crm-card crm-auto-tile${accent ? " crm-auto-tile--accent" : ""}${warn ? " crm-auto-tile--warn" : ""}`}>
      <span className="crm-label">{label}</span>
      <b className="crm-num">{value === null || value === undefined ? "—" : value}</b>
      <span className="crm-hint">{sub}</span>
    </div>
  );
}

function Select({ label, value, options, onChange }) {
  return (
    <label className="crm-auto-filter">
      <span className="crm-label">{label}</span>
      <select className="crm-select" value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * A filter that names an attribute takes SEVERAL values, ORed within itself —
 * the rule the Leads list already follows, and the reason this is a checkbox
 * popover rather than a `<select multiple>` (which needs ctrl-click and cannot
 * say "Replied, Scheduled" when closed).
 */
function Multi({ label, values, options, onChange }) {
  const [open, setOpen] = useState(false);
  const summary = !values.length
    ? "All"
    : values.length === 1
    ? options.find((option) => option.value === values[0])?.label || values[0]
    : `${values.length} selected`;

  return (
    <div className="crm-auto-filter crm-popanchor">
      <span className="crm-label">{label}</span>
      <button type="button" className="crm-select crm-select--btn" onClick={() => setOpen(!open)} aria-expanded={open}>
        {summary}
      </button>
      {open && (
        <div className="crm-pop crm-auto-filterpop">
          {options.map((option) => (
            <label key={option.value}>
              <input
                type="checkbox"
                checked={values.includes(option.value)}
                onChange={() =>
                  onChange(
                    values.includes(option.value)
                      ? values.filter((entry) => entry !== option.value)
                      : [...values, option.value]
                  )
                }
              />
              <span>{option.label}</span>
            </label>
          ))}
          {!!values.length && (
            <button type="button" className="crm-linkact" onClick={() => onChange([])}>
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * THE DRAWER. Beside the table, over its right edge, and the table does not
 * reflow — the same treatment the Leads list gives a record, because clicking
 * another row should be a way to switch records rather than a way to make the
 * page jump.
 */
/** What the record showed about whether anybody came. See callNoShow.readEvidence. */
const EVIDENCE_WORDS = {
  missed: "an empty room",
  unknown: "no transcript",
  happened: "they were there",
};

const EVIDENCE_SENTENCE = {
  missed:
    "A Fireflies transcript recorded the call and every participant on it was one of ours, which is positive proof the meeting ran and that they were not in it.",
  unknown:
    "No transcript arrived at all, and this automation is set to treat silence as a no-show. Silence also means a phone call, an uninvited bot, or a bot that failed.",
  happened: "A transcript showed somebody from their side was on the call, so the rescue was held back.",
};

function LogDrawer({ id, logId, settable, onClose, onChanged }) {
  const [log, setLog] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setLog(null);
    api(`/automations/${id}/logs/${logId}`)
      .then(setLog)
      .catch((err) => setError(err.message));
  }, [id, logId]);

  const setOutcome = async (outcome) => {
    setBusy(true);
    try {
      const updated = await api(`/automations/${id}/logs/${logId}/outcome`, {
        method: "POST",
        body: JSON.stringify({ outcome }),
      });
      setLog((current) => ({ ...current, ...updated }));
      onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <aside className="crm-auto-drawer">
        <div className="crm-card crm-card--warning">{error}</div>
      </aside>
    );
  }
  if (!log) {
    return (
      <aside className="crm-auto-drawer">
        <p className="crm-hint">Loading…</p>
      </aside>
    );
  }

  const pill = OUTCOME_PILL[log.outcome] || { label: log.outcome, key: "pending" };
  const chips = log.score_breakdown?.groups || {};
  /*
   * Read from the row's own automation type rather than from the tab's `kind`
   * prop, so a drawer cannot disagree with the row it was opened from even if
   * the two are ever fetched separately.
   */
  const isReminder = log.automation_type === "call_reminders";
  const isRescue = log.automation_type === "call_no_show";
  const missedAt = log.score_breakdown?.call_at || null;

  return (
    <aside className="crm-auto-drawer">
      <header className="crm-auto-drawer__head">
        <div>
          <b>{[log.first_name, log.last_name].filter(Boolean).join(" ") || log.email}</b>
          <span className="crm-hint">{[log.job_title, log.company_name].filter(Boolean).join(" · ")}</span>
        </div>
        <span className={`crm-pill crm-pill--${pill.key}`}>{pill.label}</span>
        <button type="button" className="crm-modal__close" onClick={onClose} aria-label="Close">
          ×
        </button>
      </header>

      {/*
        THE FACTS ARE ABOUT THE EMAIL, AND WHAT AN EMAIL IS DEPENDS ON THE
        TRIGGER. Delay and variant are the after-hours reply's own: one waits a
        randomised few minutes and one is half of an A/B, and neither is true of
        an email whose moment was fixed by a booking. Printing them as dashes
        would read as missing data rather than as questions that were never
        asked — and it would waste the three slots where the useful facts go.
      */}
      <dl className="crm-auto-drawer__facts">
        <div>
          <dt>Sent</dt>
          <dd>{log.sent_at ? dateTimeLong(log.sent_at) : "not sent"}</dd>
        </div>

        {isReminder && (
          <>
            <div>
              <dt>Which reminder</dt>
              <dd>
                {log.step_index === null || log.step_index === undefined
                  ? "—"
                  : `${log.step_index + 1}${log.step_total ? ` of ${log.step_total}` : ""}`}
                {log.sequence_label ? ` · ${log.sequence_label}` : ""}
              </dd>
            </div>
            <div>
              <dt>The call</dt>
              <dd>
                {log.call_at ? dateTimeLong(log.call_at) : "—"}
                {log.call_number ? ` · call ${log.call_number}` : ""}
              </dd>
            </div>
          </>
        )}

        {isRescue && (
          <>
            <div>
              <dt>The call missed</dt>
              <dd>{missedAt ? dateTimeLong(missedAt) : "—"}</dd>
            </div>
            <div>
              <dt>Evidence</dt>
              <dd>{EVIDENCE_WORDS[log.score_breakdown?.evidence] || "—"}</dd>
            </div>
          </>
        )}

        {!isReminder && !isRescue && (
          <>
            <div>
              <dt>Delay</dt>
              <dd className="crm-num">{log.delay_minutes === null ? "—" : `${log.delay_minutes} min`}</dd>
            </div>
            <div>
              <dt>Variant</dt>
              <dd>{log.template_variant || "—"}</dd>
            </div>
          </>
        )}
      </dl>

      {/*
        THE SCORE PANEL IS THE AFTER-HOURS REPLY'S REASON FOR EXISTING, and the
        call automations have a different one. Nothing scores a reminder — entry
        is a set of conditions and then a booking — so `score` is genuinely NULL
        rather than zero, and a panel reading "null / threshold —" above a row of
        empty chips would be the drawer explaining a decision that nothing made.
      */}
      {isReminder && (
        <section className="crm-auto-drawer__why">
          <span className="crm-label">Why this one</span>
          <p>
            {log.sequence_label ? <b>{log.sequence_label}</b> : "A sequence"} was chosen because this was
            {log.call_number ? ` call ${log.call_number}` : " a call"} with {log.company_name || "this company"}
            {log.call_timezone ? `, and the time was written in ${log.call_timezone}` : ""}
            {log.timezone_source === "fallback" ? " (we did not know their country, so this is your zone)" : ""}.
          </p>
          {log.plan_status && log.plan_status !== "active" && log.plan_stopped_reason && (
            <p className="crm-hint">{log.plan_stopped_reason}</p>
          )}
        </section>
      )}

      {isRescue && (
        <section className="crm-auto-drawer__why">
          <span className="crm-label">Why this one</span>
          <p>{EVIDENCE_SENTENCE[log.score_breakdown?.evidence] || "The call was judged a no-show."}</p>
          {log.score_breakdown?.call_title && (
            <p className="crm-hint">The call was &ldquo;{log.score_breakdown.call_title}&rdquo;.</p>
          )}
        </section>
      )}

      {!isReminder && !isRescue && (
      <section className="crm-auto-drawer__score">
        <span className="crm-label">Score at send</span>
        <div className="crm-auto-drawer__scorerow">
          <b className="crm-num">{log.score}</b>
          <span className="crm-hint">/ threshold {log.threshold_at_send ?? "—"}</span>
        </div>
        <div className="crm-auto-chips">
          {GROUP_ORDER.filter((group) => chips[group]).map((group) => [group, chips[group]]).map(([group, detail]) => (
            <span
              key={group}
              className={`crm-auto-chip${detail.contribution < 0 ? " is-negative" : ""}${
                detail.contribution === 0 ? " is-zero" : ""
              }`}
              title={detail.matched?.length ? `matched ${detail.matched.join(", ")}` : "matched nothing"}
            >
              {GROUP_CHIP[group] || group} {detail.contribution > 0 ? `+${detail.contribution}` : detail.contribution}
            </span>
          ))}
        </div>
        {/* Stored per row on purpose: the weights get edited, and this is a
            claim about the past. */}
        <p className="crm-hint">Scored with the weights as they were at the time.</p>
      </section>
      )}

      {log.blocked_reason && (
        <section className="crm-auto-refusal">
          <b>Not sent</b>
          <p>{log.blocked_reason}</p>
        </section>
      )}

      {log.rendered_body && (
        <section className="crm-auto-drawer__mail">
          <span className="crm-label">{log.outcome === "DRY_RUN" ? "What it would have sent" : "Email sent"}</span>
          <div className="crm-auto-mailcard">
            <b>{log.subject}</b>
            <pre>{log.rendered_body}</pre>
          </div>
          {!!log.slots_offered?.length && (
            <p className="crm-hint">
              {log.slots_offered.length} {log.slots_offered.length === 1 ? "time" : "times"} offered:{" "}
              {log.slots_offered.map((slot) => slot.label).join(" · ")}
            </p>
          )}
        </section>
      )}

      {log.reply && (
        <section className="crm-auto-drawer__reply">
          <div className="crm-auto-cardhead">
            <span className="crm-label">Reply</span>
            <span className="crm-hint">
              {dateTimeLong(log.reply.occurred_at)}
              {log.time_to_reply_minutes !== null && ` · ${duration(log.time_to_reply_minutes)} later`}
            </span>
          </div>
          {/* Verbatim, read from the timeline rather than copied onto the row. */}
          <blockquote>{log.reply.body}</blockquote>
        </section>
      )}

      <footer className="crm-auto-drawer__foot">
        {log.contact_id && (
          <a className="crm-btn crm-btn--secondary" href={`/contacts/${log.contact_id}`}>
            Open lead
          </a>
        )}
        <details className="crm-auto-outcome">
          <summary className="crm-btn crm-btn--secondary">Mark outcome</summary>
          <div className="crm-pop">
            {(settable || SETTABLE).map((outcome) => (
              <button
                key={outcome}
                type="button"
                className="crm-linkact"
                disabled={busy || log.outcome === outcome}
                onClick={() => setOutcome(outcome)}
              >
                {OUTCOME_PILL[outcome].label}
              </button>
            ))}
            <p className="crm-hint">
              Setting this by hand stops the poller changing it back — yours is evidence, its guess is an inference.
            </p>
          </div>
        </details>
      </footer>
    </aside>
  );
}
